/**
 * Overview page view model (#2456). Pure functions only, so every rule the
 * cards render is pinned by a unit test.
 */
import type { BoardDay, BoardStage } from '@features/dispatch';
import type { components } from '@/types/api.generated';
import type { WebhookHealthSnapshot } from '@/services/webhook-health';

/** Calendar arithmetic on YYYY-MM-DD in UTC space — no DST risk. */
export function addDays(date: string, days: number): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

function emptyDay(date: string): BoardDay {
  return {
    date,
    counts: {
      awaiting_dispatch: 0,
      awaiting_assignment: 0,
      awaiting_departure: 0,
      en_route: 0,
      delivered: 0,
      exception_or_cancelled: 0,
    },
    total: 0,
    nextGate: null,
    restDay: false,
    signals: { preflightAnomalies: 0, unassignedTasks: 0, deliveredLate: 0 },
  };
}

/** The board wire is not zero-filled: a date without delivery jobs has no row. */
function dayOf(days: readonly BoardDay[], date: string): BoardDay {
  return days.find((d) => d.date === date) ?? emptyDay(date);
}

export type StepState = 'done' | 'current' | 'pending';

export interface DeliveryDayRow {
  date: string;
  isToday: boolean;
  isTomorrow: boolean;
  restDay: boolean;
  steps: { stage: BoardStage; state: StepState }[];
  /** The stage the badge names; null when the day has no delivery jobs. */
  badge: BoardStage | null;
  delivered: number;
  total: number;
  /** The earliest gate of that day not yet past, as the board reports it. */
  nextGate: BoardDay['nextGate'];
}

/** Days after today shown besides today itself. */
const LATER_DAYS = 2;

/** The flow's columns minus the exception column: the stages a job moves through. */
export function stageChain(columns: readonly BoardStage[]): BoardStage[] {
  return columns.filter((stage) => stage !== 'exception_or_cancelled');
}

function toRow(day: BoardDay, today: string, columns: readonly BoardStage[]): DeliveryDayRow {
  const stages = stageChain(columns);
  const currentIndex = stages.findIndex((stage) => stage !== 'delivered' && day.counts[stage] > 0);
  const allDelivered = currentIndex === -1 && day.counts.delivered > 0;
  const steps = stages.map((stage, i) => {
    let state: StepState = 'pending';
    if (allDelivered || (currentIndex !== -1 && i < currentIndex)) state = 'done';
    else if (i === currentIndex) state = 'current';
    return { stage, state };
  });
  let badge: BoardStage | null = null;
  if (currentIndex !== -1) badge = stages[currentIndex] ?? null;
  else if (allDelivered) badge = 'delivered';
  else if (day.total > 0) badge = 'exception_or_cancelled';
  return {
    date: day.date,
    isToday: day.date === today,
    isTomorrow: day.date === addDays(today, 1),
    restDay: day.restDay,
    steps,
    badge,
    delivered: day.counts.delivered,
    total: day.total,
    nextGate: day.nextGate,
  };
}

/**
 * Today (always), then the two earliest later days that carry delivery jobs.
 * Steps follow the flow's columns minus the exception column.
 */
export function deliveryDayRows(
  days: readonly BoardDay[],
  today: string,
  columns: readonly BoardStage[],
): DeliveryDayRow[] {
  const later = days
    .filter((d) => d.date > today && d.total > 0)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, LATER_DAYS);
  return [dayOf(days, today), ...later].map((d) => toRow(d, today, columns));
}

export interface HeadlineCounts {
  delivered: number;
  total: number;
  /** Tomorrow's calendar date (YYYY-MM-DD). */
  tomorrow: string;
  pending: number;
}

/** Counts only — the headline never states when a dispatch happens. */
export function headlineCounts(days: readonly BoardDay[], today: string): HeadlineCounts {
  const todayRow = dayOf(days, today);
  const tomorrow = addDays(today, 1);
  return {
    delivered: todayRow.counts.delivered,
    total: todayRow.total,
    tomorrow,
    pending: dayOf(days, tomorrow).counts.awaiting_dispatch,
  };
}

type WebhookProvider = WebhookHealthSnapshot['providers'][number]['provider'];

/** Latest `lastSeenAt` among the provider's expected topics, or null. */
export function latestWebhookAt(
  snapshot: WebhookHealthSnapshot,
  provider: WebhookProvider,
): string | null {
  const seen = (snapshot.providers.find((p) => p.provider === provider)?.topics ?? [])
    .filter((t) => t.expected)
    .map((t) => t.lastSeenAt)
    .filter((at): at is string => at !== null);
  if (seen.length === 0) return null;
  return seen.reduce((latest, at) => (Date.parse(at) > Date.parse(latest) ? at : latest));
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "2 minutes ago" / "3 hours ago" in `language`, with `now` injected for tests. */
export function formatAgo(iso: string, now: Date, language: string): string {
  const elapsed = Math.max(0, now.getTime() - Date.parse(iso));
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  if (elapsed < HOUR) return rtf.format(-Math.floor(elapsed / MINUTE), 'minute');
  if (elapsed < DAY) return rtf.format(-Math.floor(elapsed / HOUR), 'hour');
  return rtf.format(-Math.floor(elapsed / DAY), 'day');
}

const TOP_DEMAND = 8;

/** Products by units descending, first eight. */
export function topDemand<T extends { units: number }>(products: readonly T[]): T[] {
  return [...products].sort((a, b) => b.units - a.units).slice(0, TOP_DEMAND);
}

type SalesBridge = components['schemas']['SalesBridge'];

export type ShopifyConsistency =
  | { kind: 'consistent' }
  | { kind: 'mismatch'; count: number }
  | { kind: 'unavailable' };

/**
 * What the sales bridge says about the Shopify menu: cannot check, N products
 * differ (all products, not only the top eight shown), or nothing differs.
 */
export function shopifyConsistency(bridge: {
  products: readonly Pick<SalesBridge['products'][number], 'anomalies'>[];
  shopify_menu?: Pick<NonNullable<SalesBridge['shopify_menu']>, 'unavailable_reason'>;
}): ShopifyConsistency {
  if (!bridge.shopify_menu || bridge.shopify_menu.unavailable_reason) {
    return { kind: 'unavailable' };
  }
  const count = bridge.products.filter((p) => (p.anomalies?.length ?? 0) > 0).length;
  return count > 0 ? { kind: 'mismatch', count } : { kind: 'consistent' };
}
